// Golf 9 on the device: birthdays and other yearly dates, and "Hoe was je dag?". Both personal, both offline.

import { useLiveQuery } from "dexie-react-hooks";
import { datesOn, nextOn } from "@shared/dates";
import { uuidv7 } from "@shared/uuid";
import { db, getMeta, type DayReflection, type ImportantDate } from "./db";
import type { Me } from "./session";
import { sync } from "./sync";

const now = () => new Date().toISOString();
const live = <T extends { deleted_at: string | null }>(rows: T[]) => rows.filter((r) => !r.deleted_at);

// ---- Verjaardagen ----

/** All dates, the first one to come on top. */
export function useImportantDates(today: string): (ImportantDate & { next: string })[] | undefined {
  return useLiveQuery(async () => {
    return live(await db.important_dates.toArray())
      .map((d) => ({ ...d, next: nextOn(d, today) }))
      .sort((a, b) => (a.next < b.next ? -1 : a.next > b.next ? 1 : a.title.localeCompare(b.title)));
  }, [today]);
}

/** What falls on a day (Vandaag, a day from the Week). */
export function useDatesOn(day: string): ImportantDate[] | undefined {
  return useLiveQuery(async () => datesOn(live(await db.important_dates.toArray()), day).today, [day]);
}

export type DateForm = Pick<ImportantDate, "title" | "kind" | "month" | "day" | "year" | "remind_days_before" | "ideas">;

export async function saveImportantDate(id: string | null, form: DateForm): Promise<void> {
  const dateId = id ?? uuidv7();
  const at = now();
  const me = await getMeta<Me>("me");
  await sync.mutate(
    "importantDate.save",
    {
      id: dateId,
      title: form.title.trim(),
      kind: form.kind,
      month: form.month,
      day: form.day,
      year: form.year,
      remindDaysBefore: form.remind_days_before,
      ideas: form.ideas?.trim() || null,
    },
    [dateId],
    async () => {
      if (id) await db.important_dates.update(id, { ...form, updated_at: at });
      else
        await db.important_dates.add({
          id: dateId,
          space_id: me?.settings?.personal_space_id ?? "",
          user_id: me?.user.id ?? "",
          ...form,
          version: 0,
          deleted_at: null,
          created_at: at,
          updated_at: at,
        });
    },
  );
}

export async function removeImportantDate(id: string): Promise<void> {
  const at = now();
  await sync.mutate("importantDate.remove", { id, at }, [id], async () => {
    await db.important_dates.update(id, { deleted_at: at });
  });
}

// ---- Hoe was je dag ----

export function useReflection(day: string): DayReflection | null | undefined {
  return useLiveQuery(async () => live(await db.day_reflections.where("day").equals(day).toArray())[0] ?? null, [day]);
}

export async function setReflection(day: string, mood: 1 | 2 | 3, note: string | null): Promise<void> {
  const existing = live(await db.day_reflections.where("day").equals(day).toArray())[0];
  const id = existing?.id ?? uuidv7();
  const at = now();
  const me = await getMeta<Me>("me");
  const clean = note?.trim() || null;
  await sync.mutate("reflection.set", { id, day, mood, note: clean }, [id], async () => {
    if (existing) await db.day_reflections.update(id, { mood, note: clean, updated_at: at });
    else
      await db.day_reflections.add({
        id,
        space_id: me?.settings?.personal_space_id ?? "",
        user_id: me?.user.id ?? "",
        day,
        mood,
        note: clean,
        version: 0,
        deleted_at: null,
        created_at: at,
        updated_at: at,
      });
  });
}
